import React, { useEffect, useState } from 'react';
import moment from 'moment';

import {
  TOUR_SHOWING_STATUS_WAITLIST,
} from 'common/tourConstants';

import {
  PRE_LEASE_LIVE_STATUS_END,
} from 'common/preLeaseConstants';

import {
  calculatePreLeaseLiveStatus,
} from 'common/preLeaseHelpers';

import useBranch from 'client/hooks/useBranch';
import PartnerActions from 'client/actions/Partner';
import abbreviateNumber from 'client/helpers/abbreviateNumber';

import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrCollapsibleRow from 'client/components/SrCollapsibleRow';
import SrPartnerStatsToursModal from 'client/components/SrPartnerStatsToursModal';
import SrPartnerStatsLeadsModal from 'client/components/SrPartnerStatsLeadsModal';
import SrDataTablePartnerStatTours from 'client/components/SrDataTablePartnerStatTours';
import SrPreLeaseStatsCard from 'client/components/SrPreLeaseStats/SrPreLeaseStatsCard';

function getPartnerListingStatsId({
  listing,
  sinceDate,
}) {
  const pathId = [`preLease-listingId:${listing.id}`];

  if (sinceDate) {
    pathId.push(`sinceDate:${sinceDate}`);
  }

  return pathId.join('_');
}

const SrPreLeaseStats = ({
  listing,
  preLease,
  locationQuery,
  onUpdatePreLease,
  onEndPreLease,
}) => {
  const sinceDate = moment().subtract(7, 'days').startOf('day').toISOString();
  const preLeaseLiveStatus = calculatePreLeaseLiveStatus({ preLease, listing });

  const [isLeadsModalOpen, setIsLeadsModalOpen] = useState(false);
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const [isToursModalOpen, setIsToursModalOpen] = useState(false);

  const {
    stats,
    stats7Days,
    dispatch,
  } = useBranch({
    stats: ['partners', 'listingStats', getPartnerListingStatsId({ listing })],
    stats7Days: ['partners', 'listingStats', getPartnerListingStatsId({ listing, sinceDate })],
  });

  useEffect(() => {
    dispatch(PartnerActions.getListingPreLeaseStats, {
      listingStatsId: getPartnerListingStatsId({ listing }),
      listingId: listing.id,
    });

    dispatch(PartnerActions.getListingPreLeaseStats, {
      listingStatsId: getPartnerListingStatsId({ listing, sinceDate }),
      listingId: listing.id,
      sinceDate: sinceDate,
    });

  }, [dispatch, listing, sinceDate]);

  if (!stats || !stats7Days) {
    return null;
  }

  const onRefreshStats = () => {
    dispatch(PartnerActions.getListingPreLeaseStats, {
      listingStatsId: getPartnerListingStatsId({ listing }),
      listingId: listing.id,
      forceRefresh: true,
    });

    dispatch(PartnerActions.getListingPreLeaseStats, {
      listingStatsId: getPartnerListingStatsId({ listing, sinceDate }),
      listingId: listing.id,
      sinceDate: sinceDate,
      forceRefresh: true,
    });
  };

  const onViewLeads = () => {
    setIsLeadsModalOpen(true);
  };

  const onViewWaitlist = () => {
    setIsWaitlistModalOpen(true);
  };

  const onViewTours = () => {
    setIsToursModalOpen(true);
  };

  return (
    <>
      <SrPreLeaseStatsCard
        listing={listing}
        preLease={preLease}
        stats={{
          views: {
            weekCount: abbreviateNumber(
              stats7Days.srViews
              + stats7Days.rentlinxViews
            ),
            totalCount: abbreviateNumber(
              stats.srViews
              + stats.rentlinxViews
            ),
          },
          leads: {
            weekCount: abbreviateNumber(
              stats7Days.srLeads
              + stats7Days.rentlinxLeads
            ),
            totalCount: abbreviateNumber(
              stats.srLeads
              + stats.rentlinxLeads
            ),
          },
          waitlist: {
            weekCount: abbreviateNumber(stats7Days.tourWaitlist),
            totalCount: abbreviateNumber(stats.tourWaitlist),
          },
          tours: preLeaseLiveStatus === PRE_LEASE_LIVE_STATUS_END ? {
            weekCount: abbreviateNumber(
              stats7Days.supraShowings
              + stats7Days.toursCompleted
            ),
            totalCount: abbreviateNumber(
              stats.supraShowings
              + stats.toursCompleted
            ),
          } : null,
        }}
        onUpdatePreLease={onUpdatePreLease}
        onEndPreLease={onEndPreLease}
        onViewLeads={onViewLeads}
        onViewWaitlist={onViewWaitlist}
        onViewTours={onViewTours}
      />

      {isLeadsModalOpen && (
        <SrPartnerStatsLeadsModal
          onClose={() => {
            setIsLeadsModalOpen(false);
          }}
          stats={stats}
          listingId={listing.id}
          onRefreshStats={onRefreshStats}
        />
      )}

      {isWaitlistModalOpen && (
        <SrDarkModal>
          <SrModalContent
            width={1340}
            dynamicHeight
            onCloseIconClick={() => {
              setIsWaitlistModalOpen(false);
            }}
          >
            <SrCollapsibleRow title={`Waitlist (${stats.tourWaitlist} unique)`}>
              <SrDataTablePartnerStatTours
                listingId={listing.id}
                sinceDate={preLease.createdDate}
                onRefreshStats={onRefreshStats}
                tourStatus={TOUR_SHOWING_STATUS_WAITLIST}
              />
            </SrCollapsibleRow>
          </SrModalContent>
        </SrDarkModal>
      )}

      {isToursModalOpen && (
        <SrPartnerStatsToursModal
          onClose={() => {
            setIsToursModalOpen(false);
          }}
          stats={stats}
          listingId={listing.id}
          locationQuery={locationQuery}
          onRefreshStats={onRefreshStats}
        />
      )}
    </>
  );
};

SrPreLeaseStats.displayName = 'SrPreLeaseStats';

export default SrPreLeaseStats;
